import {
  type DetectedCue,
  RECORDING_INSTRUCTION_MAX_LENGTH,
  RECORDING_INSTRUCTIONS_MAX,
} from "@rosc/contracts";
import { ListPlus, Plus, X } from "lucide-react";
import type { JSX } from "react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldDictationMic } from "@/components/ui/DictationButton";
import { cn } from "@/lib/utils";
import { useRecordingInstructionsStore } from "@/store/recordingInstructions.store";
import { singleLine, submitOnEnter, useAutoGrow } from "./instruction-field";

/**
 * "Add instruction" in the classic recorder (VITE_RECORDER_V32_ENABLED off):
 * a field and removable chips in that screen's look, plus "Will insert" for
 * a saved shortcut said near the end of the live transcript. Same store and
 * behaviour as the v3.2 controls (RecordingInstructions).
 */
export const ClassicRecordingInstructions = ({
  cues = [],
  className,
  dictation = false,
}: {
  cues?: DetectedCue[];
  className?: string;
  // A dictation mic in the field (VITE_DICTATION_EVERYWHERE_ENABLED). Only
  // after Stop: while recording, the recorder owns the microphone and the
  // dictated words would land in the visit audio.
  dictation?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const items = useRecordingInstructionsStore((state) => state.items);
  const add = useRecordingInstructionsStore((state) => state.add);
  const remove = useRecordingInstructionsStore((state) => state.remove);
  const skip = useRecordingInstructionsStore((state) => state.skip);
  const [text, setText] = useState("");
  const input = useRef<HTMLTextAreaElement>(null);
  const inputId = useId();
  const hintId = useId();
  const full = items.length >= RECORDING_INSTRUCTIONS_MAX;
  const submit = (): void => {
    if (add(text)) setText("");
  };
  useAutoGrow(input, text);

  return (
    <section
      aria-label={t("recordingInstructions.title")}
      className={cn("w-full space-y-2.5 font-v3 text-left", className)}
      data-testid="recording-instructions"
    >
      <div className="flex items-baseline justify-between gap-3 px-1">
        <label
          className="text-[13px] font-semibold text-v3-text"
          htmlFor={inputId}
        >
          {t("recordingInstructions.title")}
        </label>
        {items.length > 0 && (
          <span className="text-[12px] tabular-nums text-v3-text-3">
            {items.length}/{RECORDING_INSTRUCTIONS_MAX}
          </span>
        )}
      </div>
      <FieldDictationMic
        buttonClassName="absolute bottom-1.5 right-11"
        enabled={dictation && !full}
        fieldLabel={t("recordingInstructions.title")}
        getTarget={() => input.current}
      >
        {(mic) => (
          <>
            <form
              className="relative"
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <textarea
                ref={input}
                aria-describedby={hintId}
                autoComplete="off"
                className={cn(
                  "block min-h-11 w-full resize-none overflow-hidden break-words rounded-[22px] border-0 bg-v3-surface-2 py-[11px] pl-4 pr-12 text-[14px] leading-[22px] text-v3-text shadow-v3-hairline outline-none transition-colors placeholder:text-v3-text-3 hover:bg-v3-surface-3 focus:bg-v3-panel focus:ring-2 focus:ring-v3-brand-ring disabled:cursor-not-allowed disabled:opacity-60",
                  mic && "pr-[5.5rem]",
                  mic?.listening &&
                    "ring-2 ring-v3-rec/40 focus:ring-v3-rec/40",
                )}
                disabled={full}
                enterKeyHint="done"
                id={inputId}
                maxLength={RECORDING_INSTRUCTION_MAX_LENGTH}
                placeholder={
                  full
                    ? t("recordingInstructions.full", {
                        count: RECORDING_INSTRUCTIONS_MAX,
                      })
                    : t("recordingInstructions.cardPlaceholder")
                }
                rows={1}
                value={text}
                onChange={(event) => {
                  setText(singleLine(event.target.value));
                }}
                onKeyDown={(event) => {
                  submitOnEnter(event, submit);
                }}
              />
              <button
                aria-label={t("recordingInstructions.addButton")}
                className="absolute bottom-1.5 right-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-v3-brand text-v3-on-brand transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:bg-v3-surface-3 disabled:text-v3-text-3"
                disabled={full || !text.trim()}
                type="submit"
              >
                <Plus aria-hidden className="h-4 w-4" strokeWidth={2.5} />
              </button>
              {mic?.button}
            </form>
            {mic?.status}
          </>
        )}
      </FieldDictationMic>
      {(cues.length > 0 || items.length > 0) && (
        <ul
          aria-label={t("recordingInstructions.listLabel")}
          className="flex flex-wrap gap-2"
        >
          {cues.map((cue) => (
            <li
              key={`cue:${cue.trigger}`}
              className="flex min-h-8 max-w-full items-center gap-1.5 rounded-full bg-v3-brand-tint py-1 pl-2.5 pr-1 text-[13px] font-medium leading-5 text-v3-brand-strong shadow-[inset_0_0_0_1px_var(--v3-brand-ring)]"
            >
              <ListPlus aria-hidden className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 break-words">
                {t("recordingInstructions.willInsert", { name: cue.trigger })}
              </span>
              <button
                aria-label={t("recordingInstructions.dontInsert", {
                  name: cue.trigger,
                })}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-v3-brand-tint-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
                type="button"
                onClick={() => {
                  skip(cue.trigger);
                }}
              >
                <X aria-hidden className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            </li>
          ))}
          {items.map((item, index) => (
            <li
              key={`item:${item}`}
              className="flex min-h-8 max-w-full items-center gap-1 rounded-full bg-v3-brand-tint py-1 pl-3 pr-1 text-[13px] font-medium leading-5 text-v3-text shadow-v3-hairline"
            >
              <span className="min-w-0 break-words">{item}</span>
              <button
                aria-label={t("recordingInstructions.remove", { text: item })}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-v3-text-2 transition-colors hover:bg-v3-surface-3 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
                type="button"
                onClick={() => {
                  remove(index);
                }}
              >
                <X aria-hidden className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="px-1 text-[12px] leading-[17px] text-v3-text-3" id={hintId}>
        {t("recordingInstructions.helper")}
      </p>
    </section>
  );
};
